'use client';

import React, { useMemo } from 'react';
import { addYears, format, getISOWeek, getISOWeekYear } from 'date-fns';
import { dateToIsoWeek, isoWeekToRange } from './utils/format-delivery';

export type DeliveryValue =
  | { mode: 'none' }
  | { mode: 'date'; date: string }
  | { mode: 'week'; week: string };

interface Props {
  value: DeliveryValue;
  onChange: (value: DeliveryValue) => void;
}

export function DeliveryDatePicker({ value, onChange }: Props) {
  const today = useMemo(() => new Date(), []);
  const maxDate = useMemo(() => addYears(today, 1), [today]);
  const todayIso = format(today, 'yyyy-MM-dd');
  const maxIso = format(maxDate, 'yyyy-MM-dd');

  const minWeekYear = getISOWeekYear(today);
  const minWeekNum = getISOWeek(today);
  const maxWeekYear = getISOWeekYear(maxDate);
  const maxWeekNum = getISOWeek(maxDate);

  const yearOptions = useMemo(() => {
    const years = new Set<number>();
    years.add(minWeekYear);
    years.add(maxWeekYear);
    return [...years].sort((a, b) => a - b);
  }, [minWeekYear, maxWeekYear]);

  const isDateMode = value.mode === 'date';
  const isWeekMode = value.mode === 'week';

  const currentWeek = useMemo(() => {
    if (isWeekMode && value.week) {
      const [y, w] = value.week.split('-W');
      return { year: Number(y), week: Number(w) };
    }
    return { year: minWeekYear, week: minWeekNum };
  }, [isWeekMode, value, minWeekYear, minWeekNum]);

  // For the selected year, only show weeks within [minWeek..maxWeek] window.
  const weekOptions = useMemo(() => {
    const lower = currentWeek.year === minWeekYear ? minWeekNum : 1;
    const upper = currentWeek.year === maxWeekYear ? maxWeekNum : 53;
    return Array.from({ length: upper - lower + 1 }, (_, i) => i + lower);
  }, [currentWeek.year, minWeekYear, minWeekNum, maxWeekYear, maxWeekNum]);

  const weekHint = useMemo(() => {
    if (!isWeekMode) return '';
    const { start, end } = isoWeekToRange(value.week);
    return `${format(start, 'dd.MM.')} – ${format(end, 'dd.MM.yyyy')}`;
  }, [isWeekMode, value]);

  const switchToDate = () => {
    if (!isDateMode) onChange({ mode: 'date', date: '' });
  };
  const switchToWeek = () => {
    if (!isWeekMode) onChange({ mode: 'week', week: dateToIsoWeek(today) });
  };
  const clear = () => onChange({ mode: 'none' });

  const setWeekNum = (w: number) => {
    onChange({
      mode: 'week',
      week: `${currentWeek.year}-W${String(w).padStart(2, '0')}`,
    });
  };
  const setWeekYear = (y: number) => {
    let w = currentWeek.week;
    if (y === minWeekYear && w < minWeekNum) w = minWeekNum;
    if (y === maxWeekYear && w > maxWeekNum) w = maxWeekNum;
    onChange({ mode: 'week', week: `${y}-W${String(w).padStart(2, '0')}` });
  };

  return (
    <div>
      <div
        role="tablist"
        aria-label="Liefertermin-Modus"
        className="inline-flex rounded-lg border border-zinc-300 p-0.5 bg-zinc-50"
      >
        <button
          type="button"
          role="tab"
          aria-selected={isDateMode}
          onClick={switchToDate}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            isDateMode
              ? 'bg-white text-secondary shadow-sm'
              : 'text-zinc-600 hover:text-secondary'
          }`}
        >
          Datum
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={isWeekMode}
          onClick={switchToWeek}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            isWeekMode
              ? 'bg-white text-secondary shadow-sm'
              : 'text-zinc-600 hover:text-secondary'
          }`}
        >
          Kalenderwoche
        </button>
      </div>

      {isDateMode && (
        <div className="mt-2">
          <input
            type="date"
            value={value.date}
            min={todayIso}
            max={maxIso}
            onChange={(e) => onChange({ mode: 'date', date: e.target.value })}
            className="w-full px-4 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
          />
        </div>
      )}

      {isWeekMode && (
        <div className="mt-2">
          <div className="flex gap-2">
            <select
              aria-label="Kalenderwoche"
              value={currentWeek.week}
              onChange={(e) => setWeekNum(Number(e.target.value))}
              className="px-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            >
              {weekOptions.map((w) => (
                <option key={w} value={w}>
                  KW {w}
                </option>
              ))}
            </select>
            <select
              aria-label="Jahr"
              value={currentWeek.year}
              onChange={(e) => setWeekYear(Number(e.target.value))}
              className="px-3 py-2.5 border border-zinc-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-1 text-xs text-zinc-500">{weekHint}</div>
        </div>
      )}

      {value.mode !== 'none' && (
        <button
          type="button"
          onClick={clear}
          className="mt-2 text-xs text-zinc-500 hover:text-zinc-700 underline underline-offset-2"
        >
          Liefertermin entfernen
        </button>
      )}
    </div>
  );
}
